'use client';

import { Children, type CSSProperties, type HTMLProps, memo } from 'react';
import { clsx } from 'clsx';
import type { Breakpoint, Columns, Space } from '../../../styles/tokens';
import { useIntersectionObserverAction } from '../../../util/intersection-observer';
import { buildResponsiveProps, buildResponsiveVariableName, type ResponsiveValue } from '../../../util/responsive';
import { sanitizeCSSVariables } from '../../../util/style';
import styles from './grid.module.scss';

const componentStyleName = 'grid';

export type GridAutoColumnValue = number | 'childCount';

export type ResponsiveGridAutoColumns = ResponsiveValue<GridAutoColumnValue>;

export interface GridPagination {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<any>;
}

export type GridColumnDef = `${Columns}fr` | `${number}px`;

function buildDefinedColumns(columns: ResponsiveValue<GridColumnDef[]>): CSSProperties {
  return Object.entries(columns).reduce<CSSProperties>((style, [breakpoint, breakpointColumns]) => {
    style[buildResponsiveVariableName(componentStyleName, 'columns', breakpoint as Breakpoint) as keyof CSSProperties] =
      typeof breakpointColumns === 'string' ? breakpointColumns : breakpointColumns.join(' ');

    return style;
  }, {});
}

function repeatColumns(columns: ResponsiveGridAutoColumns, childCount: number): CSSProperties {
  if (typeof columns === 'number') {
    return buildResponsiveProps(componentStyleName, 'columns', `repeat(${columns}, minmax(0, 1fr))`);
  }

  if (columns === 'childCount') {
    return buildResponsiveProps(componentStyleName, 'columns', `repeat(${childCount}, minmax(0, 1fr))`);
  }

  return Object.entries(columns).reduce<CSSProperties>((style, [breakpoint, breakpointColumns]) => {
    style[buildResponsiveVariableName(componentStyleName, 'columns', breakpoint as Breakpoint) as keyof CSSProperties] =
      `repeat(${breakpointColumns === 'childCount' ? childCount : breakpointColumns}, minmax(0, 1fr))` as any;

    return style;
  }, {});
}

export interface GridProps extends HTMLProps<HTMLDivElement> {
  // `autoColumns` controls the number of columns by breakpoint
  autoColumns?: ResponsiveGridAutoColumns;
  // `columns` controls the column layout by breakpoint, this is a manual override of autoColumns, and you should use one or the other
  columns?: ResponsiveValue<GridColumnDef[]>;
  // `gap` controls the spacing between children
  gap?: ResponsiveValue<Space>;
  columnGap?: ResponsiveValue<Space>;
  rowGap?: ResponsiveValue<Space>;
  pagination?: GridPagination;
}

export const Grid = memo(({ autoColumns = 1, columns, children, className, columnGap, gap = 4, pagination, rowGap, style, ...rest }: GridProps) => {
  const childCount = Children.count(children);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pagination || {};
  const [setObservedItemRef] = useIntersectionObserverAction(fetchNextPage, {
    disabled: !hasNextPage || isFetchingNextPage || !fetchNextPage,
  });

  const baseStyle = sanitizeCSSVariables({
    ...style,
    ...(columns ? buildDefinedColumns(columns) : repeatColumns(autoColumns, childCount)),
    ...buildResponsiveProps(componentStyleName, 'row-gap', rowGap === undefined ? gap : rowGap, 'space'),
    ...buildResponsiveProps(componentStyleName, 'column-gap', columnGap === undefined ? gap : columnGap, 'space'),
  });

  return (
    <div className={clsx(styles.grid, className)} style={baseStyle} {...rest}>
      {children}
      {pagination && hasNextPage ? <div className={styles.sentry} ref={setObservedItemRef} /> : null}
    </div>
  );
});
